/**
 * FamilyLegacyScoreCard — a compact card summarising the shared
 * Social Capital score of every contributor to a given family tree.
 *
 * Fetches `/api/social-capital/family-legacy/{treeId}` and shows:
 *   • total score (sum of contributors' social_capital)
 *   • contributor count
 *   • documented members
 *   • top-5 contributor avatars/handles
 *
 * Renders nothing while loading or if the endpoint returns
 * `found: false`, so the caller can drop it in without conditionals.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import { Sparkles, Users, ScrollText } from "lucide-react";

export default function FamilyLegacyScoreCard({ treeId }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!treeId) return;
    let dead = false;
    http.get(`/social-capital/family-legacy/${treeId}`)
      .then((r) => { if (!dead) setData(r.data); })
      .catch(() => { /* silent — feature is optional */ });
    return () => { dead = true; };
  }, [treeId]);

  if (!data || !data.found) return null;

  const total = data.total_score || 0;
  const contribs = data.contributor_count || 0;
  const docd = data.documented_members || 0;
  const top = data.top_contributors || [];

  return (
    <section
      className="relative overflow-hidden rounded-2xl border p-5 sm:p-6 my-6"
      style={{
        background: "linear-gradient(135deg, #ECFDF5 0%, #F0FDF4 45%, #EFF6FF 100%)",
        borderColor: "rgba(5,150,105,0.25)",
      }}
      data-testid="family-legacy-card"
    >
      <div className="absolute -top-16 -right-16 w-52 h-52 rounded-full opacity-30 pointer-events-none"
           style={{ background: "radial-gradient(circle, #6ee7b7 0%, transparent 70%)" }} />
      <div className="relative z-10 flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="overline mb-1 flex items-center gap-1.5" style={{ color: "#065f46" }}>
            <Sparkles className="w-3 h-3" /> Family Legacy Score
          </div>
          <div className="font-display text-3xl sm:text-4xl font-bold leading-none"
               style={{ color: "#0f172a" }}
               data-testid="family-legacy-total">
            {total.toLocaleString()} <span className="text-lg opacity-60">pts</span>
          </div>
          <div className="mt-1 text-xs sm:text-sm" style={{ color: "#334155" }}>
            The combined Social Capital of everyone building this family archive.
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className="rounded-xl bg-white/80 backdrop-blur px-3 py-2 min-w-[110px]"
               data-testid="family-legacy-contribs">
            <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider opacity-70">
              <Users className="w-3 h-3" /> Contributors
            </div>
            <div className="font-mono text-xl mt-0.5" style={{ color: "#059669" }}>{contribs}</div>
          </div>
          <div className="rounded-xl bg-white/80 backdrop-blur px-3 py-2 min-w-[110px]"
               data-testid="family-legacy-docd">
            <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider opacity-70">
              <ScrollText className="w-3 h-3" /> Documented
            </div>
            <div className="font-mono text-xl mt-0.5" style={{ color: "#059669" }}>{docd}</div>
          </div>
        </div>
      </div>

      {top.length > 0 && (
        <div className="relative z-10 mt-4 pt-4 border-t"
             style={{ borderColor: "rgba(5,150,105,0.15)" }}
             data-testid="family-legacy-top">
          <div className="text-[11px] uppercase tracking-wider opacity-60 mb-2">
            Top contributors
          </div>
          <div className="flex flex-wrap gap-2">
            {top.map((u) => (
              <Link
                key={u.id}
                to={u.handle ? `/profiles/${u.handle}` : `/members/${u.id}`}
                className="inline-flex items-center gap-2 rounded-full bg-white/90 border pl-1 pr-3 py-1 text-xs hover:shadow-sm transition"
                style={{ borderColor: "rgba(0,0,0,0.08)", color: "#0f172a" }}
                data-testid={`family-legacy-user-${u.id}`}
              >
                <span
                  className="w-6 h-6 rounded-full inline-flex items-center justify-center text-[10px] font-semibold text-white"
                  style={{ background: "linear-gradient(135deg,#059669,#10b981)" }}
                >
                  {(u.name || "?").trim().charAt(0).toUpperCase()}
                </span>
                <span className="font-medium">{u.name}</span>
                <span className="font-mono opacity-60">{u.social_capital}</span>
              </Link>
            ))}
          </div>
        </div>
      )}

      {total === 0 && (
        <div className="relative z-10 mt-4 pt-4 border-t text-xs sm:text-sm"
             style={{ borderColor: "rgba(5,150,105,0.15)", color: "#334155" }}
             data-testid="family-legacy-empty">
          Scores accrue from new contributions — add a member, document an
          event, or start a heritage article and this family's legacy begins
          to grow.
        </div>
      )}

      <div className="relative z-10 mt-3">
        <Link
          to="/why-we-build"
          className="text-[11px] underline underline-offset-4 opacity-70 hover:opacity-100"
          data-testid="family-legacy-manifesto-link"
          style={{ color: "#065f46" }}
        >
          Why we build a lifetime record →
        </Link>
      </div>
    </section>
  );
}
